import {
  DUPE_API_URL,
  OPENAI_CLICK_ID_SHAPE,
  OPENAI_REGISTRATION_EVENT,
  type OpenAiConversionEventType,
} from '@dupe/common/constants'
import { getCookie } from 'cookies-next/client'
import { pruneDailyMarks } from './dailyConversionId'
import { auth } from '~/src/lib/firebase/firebaseAuthInstance'
import { isInterstitialPathname } from '~/src/lib/posthogBrowserConfig'

/**
 * The server copy of an OpenAI conversion: the browser posts each conversion
 * it decides on to the api, which forwards it to OpenAI's Conversions API
 * (`apps/api/src/controllers/openAiConversionRelay.ts`). Same event name, same
 * id as the pixel copy, so OpenAI keeps one of the pair.
 *
 * Deliberately independent of whether `window.oaiq` loaded: the pixel being
 * blocked is the case this exists for. Only the pixel's own route gate is
 * mirrored — it is never loaded on the `/redirect` interstitial
 * (`AppProviders.tsx`, `thirdPartyTagsAllowed`).
 *
 * ⚠️ The api route name is dull on purpose. Blocklists match `/track`,
 * `/conversion`, `/pixel`, `/collect`, `/event` even on a first-party host;
 * a rename to anything like those silently loses the blocked browsers.
 */
const RELAY_URL = `${DUPE_API_URL}/api/oa-relay`

/**
 * Its own once-only record, apart from the pixel's mark: that one is written
 * only when the pixel delivers, so a blocked pixel never writes it and a
 * shared record would relay every search. Swept by `pruneDailyMarks`, once
 * per page load — the marks it drops are yesterday's, not this page's.
 */
const RELAYED_PREFIX = 'openai-relayed:'

const relayed = new Set<string>()
let pruned = false

const hasRelayed = (key: string): boolean => {
  if (relayed.has(key)) return true
  try {
    return window.localStorage.getItem(`${RELAYED_PREFIX}${key}`) !== null
  } catch {
    return false
  }
}

const markRelayed = (key: string): void => {
  relayed.add(key)
  if (!pruned) {
    pruned = true
    pruneDailyMarks(
      RELAYED_PREFIX,
      `${RELAYED_PREFIX}${OPENAI_REGISTRATION_EVENT}:`
    )
  }
  try {
    window.localStorage.setItem(`${RELAYED_PREFIX}${key}`, `${Date.now()}`)
  } catch {
    // The in-page set still stops a repeat; OpenAI's id dedup covers the rest.
  }
}

const relayBody = (event: OpenAiConversionEventType, id: string): string => {
  const oppref = readOppref()
  return JSON.stringify({
    event,
    id,
    // Origin and path only — see the api's `pageUrlFor`.
    url: `${window.location.origin}${window.location.pathname}`,
    ...(oppref ? { oppref } : {}),
  })
}

/**
 * Post a search conversion. `sendBeacon` first, because the conversion often
 * fires as the tab goes away and a beacon survives the page; a text body keeps
 * it a CORS-simple request, so there is no preflight to lose. A keepalive
 * `fetch` with the same text body is the fallback when the beacon is refused.
 */
export const relayOpenAiSearchConversion = (
  event: OpenAiConversionEventType,
  id: string
): void => {
  if (typeof window === 'undefined') return
  if (isInterstitialPathname(window.location.pathname)) return
  const key = `${event}:${id}`
  if (hasRelayed(key)) return

  try {
    const body = relayBody(event, id)
    const beaconed =
      typeof navigator.sendBeacon === 'function' &&
      navigator.sendBeacon(RELAY_URL, new Blob([body], { type: 'text/plain' }))
    if (beaconed) {
      markRelayed(key)
      return
    }
    // Claimed before the await, as the signup path does, so a second call in
    // this page cannot post it again while this one is in flight.
    relayed.add(key)
    void fetch(RELAY_URL, {
      method: 'POST',
      body,
      headers: { 'content-type': 'text/plain' },
      keepalive: true,
      credentials: 'omit',
    })
      .then((res) => {
        if (res.ok) markRelayed(key)
        else relayed.delete(key)
      })
      .catch(() => {
        // Unmarked, so the next search or the pending flush tries again.
        relayed.delete(key)
      })
  } catch {
    // Analytics must not break the app.
  }
}

/**
 * Post a signup. Needs the Firebase ID token — the api only forwards a signup
 * whose id is the token's uid — so it is read from the signed-in user here,
 * and skipped when that is someone else (a later page load retries). Waits for
 * Firebase to restore the session first: the pending flush that retries a
 * failed signup runs at mount, before `currentUser` is set. A beacon
 * cannot carry a header, so this is a plain `fetch`; the tab is open for
 * signup, so that costs nothing.
 */
export const relayOpenAiRegistrationConversion = (uid: string): void => {
  if (typeof window === 'undefined') return
  if (isInterstitialPathname(window.location.pathname)) return
  const key = `${OPENAI_REGISTRATION_EVENT}:${uid}`
  if (hasRelayed(key)) return
  // Taken before the awaits so a replayed auth effect cannot post it twice.
  relayed.add(key)

  void (async () => {
    try {
      await auth.authStateReady()
      const user = auth.currentUser
      if (user?.uid === uid) {
        const token = await user.getIdToken()
        const res = await fetch(RELAY_URL, {
          method: 'POST',
          body: relayBody(OPENAI_REGISTRATION_EVENT, uid),
          headers: { 'content-type': 'text/plain', 'x-authorization': token },
          credentials: 'omit',
        })
        if (res.ok) {
          markRelayed(key)
          return
        }
      }
    } catch {
      // Falls through to release the in-page claim.
    }
    relayed.delete(key)
  })()
}

const plausible = (value: string | null | undefined): string | undefined =>
  value && OPENAI_CLICK_ID_SHAPE.test(value) ? value : undefined

/**
 * Our own copy of the click id, kept because the pixel's `__oppref` cookie is
 * never written when the pixel is blocked — which is the case the relay
 * exists for. Same lifetime and rule as the pixel's: 30 days, latest click
 * wins. Deliberately a separate key: the pixel's cookie is its own.
 */
const CLICK_ID_STORAGE_KEY = 'openai-oppref'
const CLICK_ID_TTL_MS = 30 * 24 * 60 * 60 * 1000

const urlClickId = (): string | undefined => {
  try {
    return plausible(new URLSearchParams(window.location.search).get('oppref'))
  } catch {
    return undefined
  }
}

/**
 * Store the landing URL's `?oppref=`. Called on every route change from
 * `AppProviders`, independent of the pixel, never on the `/redirect`
 * interstitial.
 */
export const rememberOpenAiClickId = (): void => {
  if (typeof window === 'undefined') return
  if (isInterstitialPathname(window.location.pathname)) return
  const value = urlClickId()
  if (!value) return
  try {
    window.localStorage.setItem(
      CLICK_ID_STORAGE_KEY,
      JSON.stringify({ value, expiresAt: Date.now() + CLICK_ID_TTL_MS })
    )
  } catch {
    // Storage unavailable: the URL and the pixel cookie still apply.
  }
}

const storedClickId = (): string | undefined => {
  try {
    const raw = window.localStorage.getItem(CLICK_ID_STORAGE_KEY)
    if (!raw) return undefined
    const { value, expiresAt } = JSON.parse(raw) as {
      value?: unknown
      expiresAt?: unknown
    }
    if (typeof expiresAt !== 'number' || expiresAt <= Date.now()) {
      window.localStorage.removeItem(CLICK_ID_STORAGE_KEY)
      return undefined
    }
    return typeof value === 'string' ? plausible(value) : undefined
  } catch {
    return undefined
  }
}

const pixelCookieClickId = (): string | undefined => {
  try {
    return plausible(getCookie('__oppref'))
  } catch {
    return undefined
  }
}

/**
 * The ad-click id, freshest source first: the current URL, our own stored
 * copy, then the pixel's `__oppref` cookie. The Conversions API cannot see
 * any of them, so it is relayed.
 */
const readOppref = (): string | undefined =>
  urlClickId() ?? storedClickId() ?? pixelCookieClickId()
